import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { publicApi } from '../utils/api';

const FxRateContext = createContext({ phpUsdRate: 0, usdPhpRate: 0, loading: true, refresh: () => {} });

const LS_KEY = 'aga_fx_rate';
const TTL_MS = 60 * 60 * 1000; // 1 hour client-side cache

export function FxRateProvider({ children }) {
  // Hydrate from localStorage so dual prices render instantly on subsequent loads
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return { phpUsdRate: 0, usdPhpRate: 0, loading: true };
      const parsed = JSON.parse(raw);
      if (parsed && parsed.ts && (Date.now() - parsed.ts) < TTL_MS && parsed.phpUsdRate > 0) {
        return {
          phpUsdRate: parsed.phpUsdRate,
          usdPhpRate: parsed.usdPhpRate || (parsed.phpUsdRate > 0 ? 1 / parsed.phpUsdRate : 0),
          loading: false,
        };
      }
    } catch (e) {
      // Cached value unreadable — fall through to background refresh.
      console.debug('FX cache read failed:', e?.message || e);
    }
    return { phpUsdRate: 0, usdPhpRate: 0, loading: true };
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await publicApi.get('/api/_meta/fx-rate');
      const phpUsdRate = Number(data?.php_usd_rate) || 0;
      const usdPhpRate = Number(data?.usd_php_rate) || (phpUsdRate > 0 ? 1 / phpUsdRate : 0);
      setState({ phpUsdRate, usdPhpRate, loading: false });
      localStorage.setItem(LS_KEY, JSON.stringify({ phpUsdRate, usdPhpRate, ts: Date.now() }));
    } catch (e) {
      console.debug('FX refresh failed (keeping cached value):', e?.message || e);
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => {
    // Always do a background refresh so values don't stay stale beyond the TTL
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);
  return <FxRateContext.Provider value={value}>{children}</FxRateContext.Provider>;
}

export function useFxRate() {
  return useContext(FxRateContext);
}
